import React, {
  MouseEventHandler,
  ReactElement,
  useState,
  useEffect,
} from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';

import paymentMethods from 'assets/svgs/icon-cards.svg';
import passengerIcon from 'assets/svgs/icon-passenger.svg';
import { useReservations } from 'contexts/reservations-context';
import { parseQuery } from 'helpers/path-helpers';
import { useTranslate } from 'hooks/translate';

import { currencyFormatter } from 'helpers/utils';
import { EUR, EUR_SYMBOL, PERCENTAGE } from 'constants/currencies';

import './booking-summary.scss';
import {
  BOOKING_IDENTIFICATION_PATH,
  BOOKING_PAYMENT_PROCESSING_PATH,
  HOMEPAGE_PATH,
} from 'constants/paths';
import { BOOKING_PAGES } from 'constants/booking';
import useNav from 'hooks/nav';
import { useAuth } from 'contexts/auth-context';
import { localizeMoment } from 'locales';
import Button from 'components/form/button/button';
import { useQuery } from 'react-query';
import { couponsApi } from 'api/bestpark/coupons';
import { CouponForm } from 'components/coupons/coupon-form';
import { calculatePriceAfterDiscount } from 'helpers/coupon-helper';

enum DiscountType {
  Percentage = 'percentage',
  Amount = 'amount',
}

interface BookingSummaryProps {
  back: MouseEventHandler;
}

interface BookingSummaryParams {
  stripeStatus: 'canceled' | 'success';
}

const DATE_FORMAT = 'D MMM';

/**
 * Handle redirection to Stripe Checkout page
 * @TODO - this should be defined at the payment option subcomponent once refactored
 * and the parent component (page) will just execute the callback enforced by an interface
 * that needs to be implemented as well
 * Sticking to this dirty approach for now
 *
 * @param {Reservation} data Reservation data
 */

export default function BookingSummary({
  back,
}: BookingSummaryProps): ReactElement {
  const [isLoading, setLoading] = useState(false);
  const [showCouponCodeField, setShowCouponCodeField] = useState(false);
  const [validCouponCode, setValidCouponCode] = useState('');
  const [finalCost, setFinalCost] = useState<number | undefined>(undefined);

  const location = useLocation();
  const { goTo } = useNav();

  const {
    endDate,
    passengerDeclarations,
    slotName,
    startDate,
    parkToReserve: data,
    costBreakdown,
    setPage,
    parkId,
    slotFeeOveride,
    isPersonBasedPricing,
    setAppliedCoupon,
    appliedCoupon,
  } = useReservations();

  const { t, locale } = useTranslate();
  const { user } = useAuth();
  const { stripeStatus } = parseQuery<BookingSummaryParams>(location.search);
  const start = moment(startDate);
  const end = moment(endDate);

  const { data: coupon, isFetching: fetchingCoupon } = useQuery(
    ['coupon', validCouponCode],
    couponsApi.getCouponByCode,
    { enabled: !!validCouponCode },
  );

  useEffect(() => {
    if (coupon) {
      setAppliedCoupon(coupon);
      setShowCouponCodeField(false);
    } else {
      setAppliedCoupon(null);
    }
  }, [coupon, setAppliedCoupon]);

  useEffect(() => {
    if (costBreakdown) {
      // Recalculate total - Grand total is getting corrupted
      // Above comment was already present in this calculation formula.
      // Study and fix this as its own issue.
      let totalCost =
        (slotFeeOveride > 0
          ? slotFeeOveride
          : costBreakdown.base.total + costBreakdown.slotCategory.total) + // camp price // parking slot price
        (isPersonBasedPricing ? costBreakdown.additionalGuests.total : 0); // additional guests

      if (appliedCoupon) {
        const { discountType, discountAmount } = appliedCoupon;
        totalCost = calculatePriceAfterDiscount(
          totalCost,
          discountType,
          discountAmount,
        );
      }

      setFinalCost(totalCost);
    }
  }, [costBreakdown, isPersonBasedPricing, slotFeeOveride, appliedCoupon]);

  const onSubmit = (): void => {
    setLoading(true);
    if (user) {
      goTo(`${BOOKING_PAYMENT_PROCESSING_PATH}`);
    } else {
      goTo(BOOKING_IDENTIFICATION_PATH);
      setPage(BOOKING_PAGES.CONTACT_DETAILS);
    }
  };

  const handleProceedWithOrWithoutCoupon = (): void => {
    if (appliedCoupon && showCouponCodeField) {
      setValidCouponCode('');
    }
    setShowCouponCodeField(!showCouponCodeField);
  };

  if (!parkId) {
    goTo(HOMEPAGE_PATH);
    return <></>;
  }

  return (
    <div className='booking-content booking-summary'>
      {stripeStatus === 'canceled' && (
        <div className='booking-alert'>
          {t('reservation.payment.paymentCancelled')}
        </div>
      )}
      <h1>{t('reservation.payment.header')}</h1>
      <div className='property-details'>
        {data?.images?.length > 0 ? (
          <div className='property-image'>
            <img
              src={`${process.env.REACT_APP_MEDIA_BASE_URL}/${data?.images[0]}`}
              alt='Camp Site Thumbnail'
            />
          </div>
        ) : null}
        <small className='reservation-date'>
          {localizeMoment(start, locale).format(DATE_FORMAT)}{' '}
          {t('reservation.payment.to')}{' '}
          {localizeMoment(end, locale).format(DATE_FORMAT)}
        </small>
        {data?.name && <h3>{data.name}</h3>}
      </div>
      <hr />
      {data.requirePassengerDeclaration && (
        <div className='passenger-details'>
          <img src={passengerIcon} alt='Passenger' />
          <p className='no-margin'>
            {t('reservation.guestInformation.passengers')}
          </p>
          <div className='passenger-names'>
            <p>{`${passengerDeclarations[0].firstName} ${passengerDeclarations[0].lastName}`}</p>
            {passengerDeclarations[0].passengerCompanion.map((companion, i) => {
              return (
                <p key={i}>{`${companion.firstName} ${companion.lastName}`}</p>
              );
            })}
          </div>
        </div>
      )}
      <hr />
      <div className='price-details'>
        <strong>{t('reservation.payment.priceDetails')}</strong>
        <table>
          <tbody>
            {!slotFeeOveride ? (
              <>
                {Object.keys(costBreakdown.breakdownNightlyRates).length > 0 ? (
                  Object.keys(costBreakdown.breakdownNightlyRates).map(
                    (breakdownPrice, key) => {
                      return (
                        <tr key={key}>
                          <td>
                            {`${currencyFormatter(EUR).format(
                              breakdownPrice,
                            )}${EUR_SYMBOL}`}{' '}
                            x{' '}
                            {t(
                              'reservation.widget.nights',
                              costBreakdown.breakdownNightlyRates[
                                breakdownPrice
                              ].length.toString(),
                            )}
                          </td>
                          <td>
                            {`${currencyFormatter(EUR).format(
                              costBreakdown.breakdownNightlyRates[
                                breakdownPrice
                              ].length * Number(breakdownPrice),
                            )}${EUR_SYMBOL}`}
                          </td>
                        </tr>
                      );
                    },
                  )
                ) : (
                  <tr>
                    <td>
                      {`${currencyFormatter(EUR).format(
                        costBreakdown.base.parkCost,
                      )}${EUR_SYMBOL} x${t(
                        'reservation.card.days',
                        costBreakdown.base.numberOfNights.toString(),
                      )}`}
                    </td>
                    <td>
                      {`${currencyFormatter(EUR).format(
                        costBreakdown.base.total,
                      )}${EUR_SYMBOL}`}
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <></>
            )}
            {costBreakdown.slotCategory.cost > 0 && (
              <tr>
                <td className='description-slot-name'>
                  {slotName} -{' '}
                  {currencyFormatter(EUR).format(
                    costBreakdown.slotCategory.cost,
                  )}
                  € x{' '}
                  {t(
                    'reservation.payment.night',
                    `${costBreakdown.slotCategory.numberOfNights}`,
                  )}
                </td>
                <td>
                  {`${currencyFormatter(EUR).format(
                    costBreakdown.slotCategory.total,
                  )}${EUR_SYMBOL}`}
                </td>
              </tr>
            )}
            {isPersonBasedPricing ? (
              <tr>
                <td>{t('reservation.payment.additionalGuests')}</td>
                <td>
                  {`${currencyFormatter(EUR).format(
                    costBreakdown.additionalGuests.total,
                  )}${EUR_SYMBOL}`}
                </td>
              </tr>
            ) : (
              <></>
            )}
            {appliedCoupon && (
              <tr>
                <td>{t('reservation.coupon.couponCode')}</td>
                <td>
                  {`- ${appliedCoupon.discountAmount}${
                    appliedCoupon.discountType === DiscountType.Percentage
                      ? PERCENTAGE
                      : EUR_SYMBOL
                  }`}
                </td>
              </tr>
            )}
            <tr>
              <td>
                <strong>{t('reservation.payment.total')}</strong>
              </td>
              <td>
                {`${
                  finalCost !== undefined
                    ? currencyFormatter(EUR).format(finalCost)
                    : ''
                }${EUR_SYMBOL}`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className='payment-coupon'>
        <Button className='link' action={handleProceedWithOrWithoutCoupon}>
          {!showCouponCodeField
            ? t('reservation.payment.coupon')
            : t('reservation.payment.continueWithoutCoupon')}
        </Button>
        {user && finalCost && showCouponCodeField ? (
          <CouponForm
            price={finalCost}
            setValidCouponCode={setValidCouponCode}
            customer={user}
          />
        ) : (
          <></>
        )}
      </div>
      <div className='booking-forms'>
        <button
          disabled={isLoading || fetchingCoupon || showCouponCodeField}
          onClick={onSubmit}
        >
          {isLoading || fetchingCoupon
            ? t('loading')
            : t('reservation.registration.next')}
        </button>
        <p className='button-information'>
          <small>{t('reservation.payment.stripePay')}</small>
          <br />
          <img src={paymentMethods} alt='Payment Methods' />
        </p>
      </div>
    </div>
  );
}
